import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Slider,
    Chip,
    Switch,
    FormControlLabel,
    Alert,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { styled, useTheme, alpha } from "@mui/material/styles";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Header from "../../components/Header";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import Fuse from "fuse.js";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { PDFDocument } from "pdf-lib";

/* ---------------- PDF.js worker ---------------- */
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/* ---------------- API ENDPOINTS ---------------- */
const API_BASE = "https://figazuresql.swancreate.com/api/swa";
const API_UPLOAD = "https://media.swancreate.com/upload";
const API_SCAN = "https://invoice.swancreate.com/scan_invoice/";
const API_DI_FALLBACK = "https://gpt5api.swancreate.com/v1/di/extract";

/* masters */
const API_VENDORS = `${API_BASE}/FINANCE_VendORS`;
const API_SCHOOLS = `${API_BASE}/SchoolMst`;
const API_CATEGORIES_UC = `${API_BASE}/FINANCE_Categories`;
const API_SUBCATS_UC = `${API_BASE}/FINANCE_Subcategories`;
const API_CATEGORIES_LC = `${API_BASE}/finance_categories`;
const API_SUBCATS_LC = `${API_BASE}/finance_subcategories`;

/* targets */
const API_HDR = `${API_BASE}/AZ_Invoice`;
const API_ITEMS = `${API_BASE}/AZ_InvoiceItems`;
const API_EXPENSES = `${API_BASE}/FINANCE_Expenses`;

/* AI */
const AI_ENDPOINT = "https://aiendpoint.swancreate.com/api/ai/gpt";

/* Fallback vendor name */
const FALLBACK_VENDOR_NAME = "Unknown Vendor";

/* ---------------- Concurrency settings ---------------- */
const OCR_CONCURRENCY = 4;
const EXPORT_ROW_CONCURRENCY = 1;
const PDF_SPLIT_CONCURRENCY = 4;
const RENDER_SCALE = 2;

/* ---------------- Styled Components ---------------- */
const Thumb = styled("img")(({ theme }) => ({
    width: 56,
    height: 56,
    objectFit: "cover",
    cursor: "pointer",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0 0 8px ${theme.palette.secondary.main}66`,
}));

const DropZone = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.secondary.main}`,
    borderRadius: 8,
    padding: 20,
    textAlign: "center",
    color: theme.palette.secondary.main,
    cursor: "pointer",
    marginBottom: 16,
}));

const reviewGlowSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    border: "1px solid #00E5FF",
    color: "#E6F7FF",
    background:
        "linear-gradient(135deg, rgba(0,229,255,.15) 0%, rgba(0,136,255,.15) 100%)",
    boxShadow: "0 0 0 0 rgba(0,229,255,0), inset 0 0 0 0 rgba(0,229,255,0)",
    transition:
        "box-shadow 180ms ease, transform 120ms ease, background-color 180ms ease",
    "&:hover": {
        background:
            "linear-gradient(135deg, rgba(0,229,255,.22) 0%, rgba(0,136,255,.22) 100%)",
        boxShadow:
            "0 0 14px 3px rgba(0,229,255,.85), 0 0 30px 10px rgba(0,136,255,.45)",
    },
    "&:focus-visible": {
        outline: "2px solid #00E5FF",
        boxShadow:
            "0 0 0 4px rgba(0,229,255,.35), 0 0 36px 10px rgba(0,136,255,.35)",
    },
    "&:active": {
        transform: "translateY(1px)",
        boxShadow: "0 0 12px 3px rgba(0,229,255,.75)",
    },
};

/* ---------------- Utility Functions ---------------- */
const pickScalar = (x) =>
    x && typeof x === "object" ? (x.value ?? x.text ?? x.raw ?? null) : x ?? null;

const asString = (x, maxLen) => {
    const v = pickScalar(x);
    const s = v == null ? "" : String(v).trim();
    return typeof maxLen === "number" ? s.slice(0, maxLen) : s;
};

const safeNumber = (v) => {
    if (v == null || v === "") return 0;
    const n = Number(String(v).replace(/[^\d.\-]/g, ""));
    return Number.isFinite(n) ? n : 0;
};
const asNumber = (x) => safeNumber(pickScalar(x));

const asDate = (x) => {
    const v = pickScalar(x);
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
};
const asDateTime = (x) => {
    const v = pickScalar(x);
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const normalize = (str) =>
    (str || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const normSchool = (s) =>
    normalize(s)
        .replace(
            /\b(attn|attention|ship|to|recipient|deliver|delivery|address|kitchen|cafeteria|food\s?svc|nutrition|dept|department)\b/g,
            ""
        )
        .replace(/^[a-z]?\d{1,4}\b\s*/i, "")
        .replace(/\b(elementary|elem|el)\b/g, "es")
        .replace(/\b(middle|junior high|jr high|intermediate)\b/g, "ms")
        .replace(/\b(high|senior high)\b/g, "hs")
        .replace(/\b(primary)\b/g, "ps")
        .replace(/\b(charter|academy|school|schools|public|district|sd|isd|usd)\b/g, "")
        .replace(/\s+/g, " ")
        .trim();

const makeAliases = (name) => {
    const base = normalize(name);
    const compact = base.replace(/\b(school|schools)\b/g, "").trim();
    const abbr = normSchool(name);
    const noPunct = base.replace(/[^a-z0-9]+/g, "");
    return Array.from(new Set([base, compact, abbr, noPunct].filter(Boolean)));
};

async function fetchJsonFallback(urls) {
    for (const u of urls) {
        try {
            const { data } = await axios.get(u);
            if (Array.isArray(data)) return data;
            if (data) return data;
        } catch {
            /* try next */
        }
    }
    throw new Error(`All endpoints failed: ${urls.join(", ")}`);
}

function parseFirstJsonObject(text) {
    const s = String(text || "");
    const start = s.indexOf("{");
    if (start === -1) throw new Error("No JSON object found.");
    let depth = 0;
    for (let i = start; i < s.length; i++) {
        if (s[i] === "{") depth++;
        else if (s[i] === "}") {
            depth--;
            if (depth === 0) return JSON.parse(s.slice(start, i + 1));
        }
    }
    throw new Error("Unterminated JSON object.");
}

/* Transform DI response to invoice_data format */
function transformDiToInvoiceData(di) {
    if (!di) return [];

    const kv = Array.isArray(di.keyValuePairs) ? di.keyValuePairs : [];
    const tables = Array.isArray(di.tables) ? di.tables : [];

    const findKV = (needle) => {
        const m = (needle || "").toLowerCase();
        const entry = kv.find((p) => (p.key || "").toLowerCase().includes(m));
        return entry?.value || "";
    };

    const invoiceNumber = findKV("invoice number");
    const invoiceDate = findKV("invoice date");
    const billTo = findKV("bill to");
    const shipTo = findKV("ship to");
    const remitTo = findKV("remit to");

    let vendorName = "";
    if (remitTo) {
        vendorName = remitTo.split("\n")[0].trim();
    }

    const customerName = shipTo || billTo || "";

    const totalKeys = [
        "delivered amount",
        "product total",
        "delivery amount",
        "as shipped delivery amount",
        "invoice total",
    ];
    let totalRaw = "";
    for (const k of totalKeys) {
        const v = findKV(k);
        if (v && /\d/.test(String(v))) {
            totalRaw = v;
            break;
        }
    }
    const invoiceTotal = safeNumber(totalRaw);

    const items = [];
    for (const t of tables) {
        const rows = Array.isArray(t.rows) ? t.rows : [];
        if (!rows.length) continue;

        const flat = rows.map((r) => r.join(" ")).join(" ");
        const hasInvoiceLines = /invoice line details/i.test(flat);
        if (!hasInvoiceLines) continue;

        for (const row of rows) {
            if (!Array.isArray(row) || row.length < 6) continue;

            const hasMoney = row.some(
                (cell) => typeof cell === "string" && cell.includes("$")
            );
            if (!hasMoney) continue;

            const qty = safeNumber(row[1] || row[0]);
            const unit = asString(row[3], 50);
            const productCode = asString(row[4], 100);
            const desc = asString(row[5], 250);
            const unitPrice = safeNumber(row[row.length - 2]);
            const amount = safeNumber(row[row.length - 1]);

            if (!qty && !amount && !desc) continue;

            items.push({
                Description: desc,
                Quantity: qty,
                Unit: unit,
                UnitPrice: unitPrice,
                Amount: amount,
                ProductCode: productCode,
            });
        }

        if (items.length) break;
    }

    return [
        {
            InvoiceId: invoiceNumber,
            InvoiceNumber: invoiceNumber,
            InvoiceDate: invoiceDate,
            InvoiceTotal: invoiceTotal || null,
            CustomerName: customerName,
            VendorName: vendorName,
            ShippingAddressRecipient: shipTo || "",
            Items: items,
        },
    ];
}

/* Concurrency limiter */
function pLimit(max) {
    const queue = [];
    let active = 0;
    const next = () => {
        active--;
        if (queue.length) queue.shift()();
    };
    return (fn) =>
        new Promise((resolve, reject) => {
            const run = async () => {
                active++;
                try {
                    const res = await fn();
                    resolve(res);
                } catch (e) {
                    reject(e);
                } finally {
                    next();
                }
            };
            if (active < max) run();
            else queue.push(run);
        });
}

/* Ship-To extraction */
function extractShipTo(inv) {
    const recipient = asString(inv?.ShippingAddressRecipient);
    if (recipient) return recipient.trim();

    const parts = [];
    const tryPush = (v) => {
        const t = asString(v);
        if (t) parts.push(t);
    };

    tryPush(inv.ShipTo);
    tryPush(inv.ShipToName);
    tryPush(inv.ShipToParty);
    tryPush(inv.ShipToAddress);
    tryPush(inv.ShippingAddress);
    tryPush(inv.DeliveryAddress);
    tryPush(inv.DeliveryTo);
    tryPush(inv.Recipient);
    tryPush(inv.CustomerAddressRecipient);
    tryPush(inv.CustomerShipTo);
    tryPush(inv.ShipTo_Recipient);
    tryPush(inv.Ship_To);
    tryPush(inv.ShipToCustomer);
    tryPush(inv.ShipToBlock);
    tryPush(inv.ShipToText);
    tryPush(inv.CustomerName);

    return parts.join(" ").replace(/\s+/g, " ").trim();
}

/* Expense helpers */
const getExpenseId = (exp) =>
    exp?.ExpenseID ?? exp?.ExpenseId ?? exp?.expenseid ?? exp?.id ?? null;

const makeExpenseKey = (vendorName, invoiceNumber) =>
    `${normalize(vendorName)}|inv:${normalize(String(invoiceNumber || ""))}`;

/* Sleep helper */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/* ---------------- Main Component ---------------- */
export default function FIGAgentInvoiceBulkReview() {
    const theme = useTheme();

    /* State */
    const [vendors, setVendors] = useState([]);
    const [schools, setSchools] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [files, setFiles] = useState([]);
    const [pdfPages, setPdfPages] = useState([]);
    const [rows, setRows] = useState([]);

    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [exporting, setExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [error, setError] = useState("");
    const [exportMessage, setExportMessage] = useState("");

    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryRow, setGalleryRow] = useState(null);
    const [galleryIndex, setGalleryIndex] = useState(0);

    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const [reviewErrors, setReviewErrors] = useState({});
    const [zoom, setZoom] = useState(1);
    const [reviewIndex, setReviewIndex] = useState(0);

    const expenseIndexRef = useRef(new Map());
    const [expenseIndexLoaded, setExpenseIndexLoaded] = useState(false);
    const [skipExistingOnExport, setSkipExistingOnExport] = useState(false);

    const exportedRef = useRef(new Set());
    const keyToApiRef = useRef(new Map());
    const splittingRef = useRef(0);
    const processCountRef = useRef(0);
    const aiSchoolCache = useRef(new Map());
    const aiCategoryCache = useRef(new Map());
    const mountedRef = useRef(true);

    useEffect(() => () => { mountedRef.current = false; }, []);

    /* Lookups */
    const subsByCat = useMemo(() => {
        const m = new Map();
        (subcategories || []).forEach((s) => {
            const key = String(s.CategoryID);
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(s);
        });
        return m;
    }, [subcategories]);

    const schoolFuse = useMemo(() => {
        if (!schools.length) return null;
        const recs = schools.map((s) => ({ ...s, __aliases: makeAliases(s.SchoolName || "") }));
        return new Fuse(recs, {
            keys: ["SchoolName", "__aliases"],
            threshold: 0.26,
            ignoreLocation: true,
            includeScore: true,
            minMatchCharLength: 2,
        });
    }, [schools]);

    const catFuse = useMemo(
        () => (categories.length ? new Fuse(categories, { keys: ["CategoryName"], threshold: 0.3 }) : null),
        [categories]
    );

    /* Load masters */
    useEffect(() => {
        axios.get(API_VENDORS).then((r) => setVendors(r.data || [])).catch(console.error);
        axios.get(API_SCHOOLS).then((r) => setSchools(r.data || [])).catch(console.error);
        (async () => {
            try {
                const [cats, subs] = await Promise.all([
                    fetchJsonFallback([API_CATEGORIES_UC, API_CATEGORIES_LC]),
                    fetchJsonFallback([API_SUBCATS_UC, API_SUBCATS_LC]),
                ]);
                setCategories(cats || []);
                setSubcategories(subs || []);
            } catch (e) {
                console.error("Category/subcategory load failed:", e);
            }
        })();
    }, []);

    /* Matching functions */
    function matchVendor(rawOCR) {
        const key = normalize(asString(rawOCR));
        if (!key) {
            const fallback = vendors.find((v) => normalize(v.VendorName) === normalize(FALLBACK_VENDOR_NAME));
            return fallback || { VendorID: null, VendorName: FALLBACK_VENDOR_NAME };
        }
        let v =
            vendors.find((v) => normalize(v.VendorName).includes(key) || key.includes(normalize(v.VendorName))) ||
            vendors.find((v) => normalize(v.VendorName) === key);
        return (
            v ||
            vendors.find((v) => normalize(v.VendorName) === normalize(FALLBACK_VENDOR_NAME)) || {
                VendorID: null,
                VendorName: FALLBACK_VENDOR_NAME,
            }
        );
    }

    function matchSchoolFromShipTo(shipToText) {
        const qRaw = (shipToText || "").toString().trim();
        if (!qRaw) return null;

        const qLower = qRaw.toLowerCase();
        const qNorm = normSchool(qRaw);
        const qAliases = makeAliases(qRaw);

        let s = schools.find((x) => (x.SchoolName || "").toLowerCase() === qLower) || null;
        if (s) return s;

        s =
            schools.find((x) => {
                const n = (x.SchoolName || "").toLowerCase();
                return n.includes(qLower) || qLower.includes(n);
            }) || null;
        if (s) return s;

        s = schools.find((x) => normSchool(x.SchoolName || "") === qNorm) || null;
        if (s) return s;

        if (schoolFuse) {
            for (const q of [qRaw, qNorm, ...qAliases]) {
                const res = schoolFuse.search(q);
                if (res?.length && res[0].score <= 0.35) return res[0].item;
            }
        }
        return null;
    }

    /* AI helpers */
    async function aiClassify(inv) {
        const itemsArr = Array.isArray(inv.Items)
            ? inv.Items.map((it) => (it.Description?.value || it.Description || "")).filter(Boolean).slice(0, 25)
            : [];
        const cacheKey = `${inv.VendorName || ""}|${itemsArr.join("|")}`.toLowerCase();
        if (aiCategoryCache.current.has(cacheKey)) return aiCategoryCache.current.get(cacheKey);

        try {
            const catList = categories.slice(0, 200).map((c) => ({ id: c.CategoryID, name: c.CategoryName }));
            const subList = subcategories.slice(0, 800).map((s) => ({
                id: s.SubcategoryID,
                name: s.SubcategoryName,
                categoryId: s.CategoryID,
            }));

            const prompt = [
                "Decide a Category and Subcategory for this invoice based on vendor and item descriptions.",
                "Choose ONLY from the provided lists. Return EXACT JSON object:",
                '{ "categoryName": "<string>", "subcategoryName": "<string|null>" }',
                "",
                "Vendor:", inv.VendorName || "",
                "Items:", JSON.stringify(itemsArr),
                "",
                "Categories:", JSON.stringify(catList),
                "Subcategories:", JSON.stringify(subList),
            ].join("\n");

            const { data } = await axios.post(AI_ENDPOINT, { prompt });
            const raw = typeof data === "string" ? data : (data.response ?? data);
            const txt = String(raw).replace(/```json|```/g, "").trim();
            const obj = txt.startsWith("{") ? JSON.parse(txt) : parseFirstJsonObject(txt);
            const result = {
                categoryName: (obj.categoryName || "").toString().trim(),
                subcategoryName: (obj.subcategoryName || "").toString().trim() || null,
            };
            aiCategoryCache.current.set(cacheKey, result);
            return result;
        } catch {
            const result = { categoryName: "", subcategoryName: null };
            aiCategoryCache.current.set(cacheKey, result);
            return result;
        }
    }

    async function aiPickSchool(shipToText) {
        const key = normSchool(shipToText || "");
        if (aiSchoolCache.current.has(key)) return aiSchoolCache.current.get(key);

        try {
            if (!shipToText) return null;

            const schoolList = (schools || []).map((s) => ({
                id: s.SchoolId,
                name: s.SchoolName,
            })).slice(0, 1200);

            const prompt = [
                "You are matching a shipping recipient string to a school/site from a provided list.",
                "Pick EXACTLY ONE school from the list. If you are unsure, pick the closest.",
                "Return EXACT JSON ONLY:",
                '{ "schoolId": <number|null>, "schoolName": "<exact name from list or empty>" }',
                "",
                "Recipient (from invoice ShippingAddressRecipient.value):",
                JSON.stringify(shipToText),
                "",
                "Schools (choose from this list only):",
                JSON.stringify(schoolList),
                "",
                "Rules:",
                "- Normalize abbreviations (ES, MS, HS) and ignore routing codes like 'A5', 'B12'.",
                "- Prefer exact name if present; otherwise choose the closest reasonable match.",
                "- The schoolName MUST be copied exactly from the list; do not invent names.",
                "- If no obvious match, still choose the most likely.",
            ].join("\n");

            const { data } = await axios.post(AI_ENDPOINT, { prompt });
            const raw = typeof data === "string" ? data : (data.response ?? data);
            const txt = String(raw).replace(/```json|```/g, "").trim();
            const obj = txt.startsWith("{") ? JSON.parse(txt) : parseFirstJsonObject(txt);
            const name = (obj.schoolName || "").toString().trim();
            const id = obj.schoolId ?? null;

            if (!name) { aiSchoolCache.current.set(key, null); return null; }

            const exact = schools.find((s) => (s.SchoolName || "").toLowerCase() === name.toLowerCase());
            if (exact) { const res = { SchoolId: exact.SchoolId, SchoolName: exact.SchoolName }; aiSchoolCache.current.set(key, res); return res; }

            if (id != null) {
                const byId = schools.find((s) => String(s.SchoolId) === String(id));
                if (byId) { const res = { SchoolId: byId.SchoolId, SchoolName: byId.SchoolName }; aiSchoolCache.current.set(key, res); return res; }
            }

            const loose = schools.find((s) => (s.SchoolName || "").toLowerCase().includes(name.toLowerCase()));
            if (loose) { const res = { SchoolId: loose.SchoolId, SchoolName: loose.SchoolName }; aiSchoolCache.current.set(key, res); return res; }

            aiSchoolCache.current.set(key, null);
            return null;
        } catch {
            aiSchoolCache.current.set(key, null);
            return null;
        }
    }

    /* Grouping helpers */
    const getInvoiceNo = (src) =>
        (src.InvoiceId?.value || src.InvoiceId || src.InvoiceNumber?.value || src.InvoiceNumber || "").toString().trim();

    const primaryKey = (src) => {
        const vendor = normalize(src.VendorName?.value || src.VendorName || "");
        const inv = normalize(getInvoiceNo(src));
        if (inv) return `${vendor}|inv:${inv}`;
        const po = normalize(src.PurchaseOrder?.value || src.PurchaseOrder || "");
        if (po) return `${vendor}|po:${po}`;
        const date = normalize(asDate(src.InvoiceDate?.value || src.InvoiceDate || ""));
        const amt = String(src.InvoiceTotal?.value ?? src.InvoiceTotal ?? "").replace(/[^\d.]/g, "");
        return `${vendor}|date:${date}|amt:${amt}`;
    };

    /* Merge helpers */
    const normalizeItems = (items) => {
        if (!Array.isArray(items)) return [];
        return items.map((rawItem) => ({
            Description: asString(rawItem.Description),
            Quantity: asNumber(rawItem.Quantity),
            Unit: asString(rawItem.Unit, 50),
            UnitPrice: asNumber(rawItem.UnitPrice),
            ContractPrice: asNumber(rawItem.ContractPrice),
            ProductCode: asString(rawItem.ProductCode, 100),
            Date: asDateTime(rawItem.Date),
            Tax: asString(rawItem.Tax, 50),
            Amount: asNumber(rawItem.Amount),
        }));
    };

    const sumItems = (arr) => (Array.isArray(arr) ? arr.reduce((t, i) => t + safeNumber(i.Amount), 0) : 0);

    const mergeRows = (existing, incoming, pageInfo) => {
        const merged = { ...existing };

        merged.InvoiceNumber = merged.InvoiceNumber || getInvoiceNo(incoming);
        merged.CustomerName = merged.CustomerName || asString(incoming.CustomerName);
        merged.InvoiceDate = merged.InvoiceDate || asDate(incoming.InvoiceDate);

        merged.VendorID = merged.VendorID || incoming.VendorID || null;
        merged.VendorName = merged.VendorName || incoming.VendorName || "";
        merged.SchoolId = merged.SchoolId || incoming.SchoolId || null;
        merged.SchoolName = merged.SchoolName || incoming.SchoolName || "";

        merged.CategoryID = merged.CategoryID || incoming.CategoryID || null;
        merged.CategoryName = merged.CategoryName || incoming.CategoryName || "";
        merged.SubcategoryID = merged.SubcategoryID || incoming.SubcategoryID || null;
        merged.SubcategoryName = merged.SubcategoryName || incoming.SubcategoryName || "";

        merged.__existingExpense = merged.__existingExpense || !!incoming.__existingExpense || false;
        merged.__existingExpenseId = merged.__existingExpenseId || incoming.__existingExpenseId || null;
        merged.__existingExpenseRaw = merged.__existingExpenseRaw || incoming.__existingExpenseRaw || null;

        const prevItems = Array.isArray(merged.Items) ? merged.Items : [];
        const newItems = normalizeItems(incoming.Items);
        const itemKey = (it) => `${(it.Description || "").toLowerCase()}|${it.Amount}|${it.UnitPrice}|${it.Quantity}`;
        const seen = new Set(prevItems.map(itemKey));
        for (const it of newItems) {
            const k = itemKey(it);
            if (!seen.has(k)) {
                prevItems.push(it);
                seen.add(k);
            }
        }
        merged.Items = prevItems;

        const incTotal = safeNumber(incoming.InvoiceTotal);
        const haveExplicit = safeNumber(merged.InvoiceTotal) > 0 || incTotal > 0;
        merged.InvoiceTotal = haveExplicit
            ? Math.max(safeNumber(merged.InvoiceTotal), incTotal)
            : sumItems(merged.Items);

        const pages = Array.isArray(merged.pageImages) ? merged.pageImages : [];
        if (pageInfo?.url) {
            const already = pages.some((p) => p.url === pageInfo.url);
            if (!already) pages.push({ url: pageInfo.url, pageNum: pageInfo.pageNum || pages.length + 1, blob: pageInfo.blob || null });
        }
        merged.pageImages = pages;
        merged.imageURL = merged.imageURL || pageInfo?.url || "";
        merged.PageNum = Math.max(merged.PageNum || 1, pageInfo?.pageNum || 1);

        return merged;
    };

    /* File handling */
    const fileInputRef = useRef(null);

    const handleFiles = (e) => {
        Array.from(e.target.files).forEach((f) => {
            if (f.type === "application/pdf" || /\.pdf$/i.test(f.name)) {
                splitPdf(f);
            } else {
                setFiles((prev) => [...prev, f]);
            }
        });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFiles({ target: { files: e.dataTransfer.files } });
    };

    const splitPdf = async (file) => {
        try {
            const buf = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
            const limiter = pLimit(PDF_SPLIT_CONCURRENCY);
            const promises = [];
            for (let i = 1; i <= pdf.numPages; i++) {
                splittingRef.current += 1;
                promises.push(
                    limiter(async () => {
                        try {
                            const page = await pdf.getPage(i);
                            const vp = page.getViewport({ scale: RENDER_SCALE });
                            const canvas = document.createElement("canvas");
                            canvas.width = vp.width;
                            canvas.height = vp.height;
                            await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
                            const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg"));
                            setPdfPages((prev) => [
                                ...prev,
                                { file: new File([blob], `${file.name}_p${i}.jpg`, { type: "image/jpeg" }), PageNum: i },
                            ]);
                        } finally {
                            splittingRef.current -= 1;
                        }
                    })
                );
            }
            await Promise.all(promises);
        } catch (e) {
            console.error("PDF split error:", e);
        }
    };

    const waitForSplits = async () => {
        let settle = 0;
        while ((splittingRef.current > 0 || settle < 2) && settle < 20) {
            await sleep(150);
            if (splittingRef.current === 0) settle += 1;
        }
    };

    /* PDF building */
    async function buildAndUploadInvoicePdf(row) {
        try {
            const pages = Array.isArray(row.pageImages)
                ? row.pageImages.slice().sort((a, b) => (a.pageNum || 0) - (b.pageNum || 0))
                : [];
            if (!pages.length) return null;

            const pdfDoc = await PDFDocument.create();

            for (const p of pages) {
                const blob = p.blob;
                if (!blob) {
                    if (p.url && !p.url.startsWith("blob:")) {
                        try {
                            const resp = await fetch(p.url);
                            const ab = await resp.arrayBuffer();
                            try {
                                const imgJ = await pdfDoc.embedJpg(ab);
                                const dims = imgJ.scale(1);
                                const page = pdfDoc.addPage([dims.width, dims.height]);
                                page.drawImage(imgJ, { x: 0, y: 0, width: dims.width, height: dims.height });
                            } catch {
                                const imgP = await pdfDoc.embedPng(ab);
                                const dims = imgP.scale(1);
                                const page = pdfDoc.addPage([dims.width, dims.height]);
                                page.drawImage(imgP, { x: 0, y: 0, width: dims.width, height: dims.height });
                            }
                        } catch (e) {
                            console.warn("Skipped remote image:", p.url);
                        }
                    }
                    continue;
                }
                const ab = await blob.arrayBuffer();
                try {
                    const img = await pdfDoc.embedJpg(ab);
                    const dims = img.scale(1);
                    const page = pdfDoc.addPage([dims.width, dims.height]);
                    page.drawImage(img, { x: 0, y: 0, width: dims.width, height: dims.height });
                } catch {
                    const img = await pdfDoc.embedPng(ab);
                    const dims = img.scale(1);
                    const page = pdfDoc.addPage([dims.width, dims.height]);
                    page.drawImage(img, { x: 0, y: 0, width: dims.width, height: dims.height });
                }
            }

            const pdfBytes = await pdfDoc.save();
            const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
            const filename = `${uuidv4()}.pdf`;

            const form = new FormData();
            form.append("file", pdfBlob, filename);
            form.append("folder", "invoices");

            await axios.post(API_UPLOAD, form);
            const pdfURL = `https://media.swancreate.com/images/invoices/${filename}`;
            return pdfURL;
        } catch (e) {
            console.error("PDF combine/upload failed:", e);
            return null;
        }
    }

    const revokeObjectURLsForRow = (row) => {
        if (Array.isArray(row?.pageImages)) {
            for (const p of row.pageImages) {
                if (p?.url && p.url.startsWith("blob:")) {
                    try { URL.revokeObjectURL(p.url); } catch { /* noop */ }
                }
            }
        }
        if (row?.imageURL && row.imageURL.startsWith("blob:")) {
            try { URL.revokeObjectURL(row.imageURL); } catch { /* noop */ }
        }
    };

    /* Load expense index */
    const loadExpenseIndex = async () => {
        try {
            const data = await fetchJsonFallback([
                `${API_EXPENSES}?_limit=50000`,
                `${API_EXPENSES}?limit=50000`,
                API_EXPENSES,
            ]);
            const list = Array.isArray(data) ? data : (data?.data || []);
            const map = new Map();
            for (const exp of list || []) {
                const inv = (exp.InvoiceNumber ?? exp.invoiceNumber ?? exp.InvoiceId ?? exp.invoiceId ?? "").toString().trim();
                if (!inv) continue;
                const vname = (exp.VendorName ?? exp.vendorName ?? exp.Vendor ?? "").toString().trim();
                const key = makeExpenseKey(vname, inv);
                if (!map.has(key)) map.set(key, exp);
                const looseKey = makeExpenseKey("", inv);
                if (!map.has(looseKey)) map.set(looseKey, exp);
            }
            expenseIndexRef.current = map;
            setExpenseIndexLoaded(true);
        } catch (e) {
            console.error("Failed to load Expense index:", e);
            expenseIndexRef.current = new Map();
            setExpenseIndexLoaded(false);
        }
    };

    /* Process invoices with OCR */
    const handleProcess = async () => {
        if (!files.length && !pdfPages.length) {
            setError("No files to process.");
            return;
        }
        setError("");
        setLoading(true);
        setProgress(0);
        processCountRef.current = 0;

        await loadExpenseIndex();
        await waitForSplits();

        const tasks = [
            ...files.map((f) => ({ file: f, page: null })),
            ...pdfPages.map((p) => ({ file: p.file, page: p })),
        ];
        const limiter = pLimit(OCR_CONCURRENCY);

        const doOne = (task) =>
            limiter(async () => {
                const { file, page } = task;
                const localURL = URL.createObjectURL(file);

                try {
                    const sf = new FormData();
                    sf.append("file", file, file.name);

                    let data = null;
                    let primaryError = null;

                    // Primary OCR
                    try {
                        const primaryResp = await axios.post(API_SCAN, sf);
                        data = primaryResp?.data || null;

                        if (data && Array.isArray(data.invoice_data) && data.invoice_data.length > 0) {
                            console.log(`Primary OCR succeeded for ${file.name}`);
                        }
                    } catch (err) {
                        primaryError = err;
                        console.error(`Primary OCR failed for ${file.name}:`, err?.message || err);
                    }

                    // Fallback to DI if primary failed
                    if (
                        !data ||
                        !Array.isArray(data.invoice_data) ||
                        !data.invoice_data.length
                    ) {
                        console.log(`Trying fallback OCR for ${file.name}...`);
                        try {
                            const sf2 = new FormData();
                            sf2.append("file", file, file.name);
                            const fallbackResp = await axios.post(
                                API_DI_FALLBACK,
                                sf2,
                                {
                                    headers: { accept: "application/json" },
                                }
                            );
                            const invoice_data = transformDiToInvoiceData(
                                fallbackResp.data
                            );
                            data = { invoice_data };
                            console.log(`Fallback OCR succeeded for ${file.name}`);
                        } catch (diErr) {
                            console.error(
                                `Secondary OCR also failed for ${file.name}:`,
                                diErr?.message || diErr
                            );
                            if (primaryError) {
                                throw primaryError;
                            }
                            throw diErr;
                        }
                    }

                    const invs = (data && data.invoice_data) || [];
                    if (!Array.isArray(invs) || !invs.length) {
                        console.warn(`No invoices found in ${file.name}`);
                        return;
                    }

                    await Promise.all(
                        invs.map(async (raw) => {
                            const inv = { ...raw };
                            inv.InvoiceNumber = getInvoiceNo(inv);
                            inv.InvoiceDate = asDate(inv.InvoiceDate);
                            inv.InvoiceTotal = safeNumber(inv.InvoiceTotal?.value ?? inv.InvoiceTotal);

                            const v = matchVendor(inv.VendorName?.value || inv.VendorName);
                            inv.VendorID = v?.VendorID || null;
                            inv.VendorName = v?.VendorName || (inv.VendorName?.value || inv.VendorName || "");

                            const shipTo = extractShipTo(inv);
                            const [picked, ai] = await Promise.all([aiPickSchool(shipTo), aiClassify(inv)]);

                            const schoolPicked = picked || matchSchoolFromShipTo(shipTo);
                            inv.SchoolId = schoolPicked?.SchoolId || null;
                            inv.SchoolName = schoolPicked?.SchoolName || "Unknown School";
                            inv.CustomerName = shipTo || inv.CustomerName || "";

                            let cat = null;
                            if (ai.categoryName) {
                                cat =
                                    categories.find((c) => c.CategoryName.toLowerCase() === ai.categoryName.toLowerCase()) ||
                                    (catFuse ? catFuse.search(ai.categoryName)[0]?.item : null) ||
                                    null;
                            }
                            if (cat) {
                                inv.CategoryID = cat.CategoryID;
                                inv.CategoryName = cat.CategoryName;
                                const list = subsByCat.get(String(cat.CategoryID)) || [];
                                let sub = null;
                                if (ai.subcategoryName) {
                                    sub =
                                        list.find(
                                            (su) => su.SubcategoryName.toLowerCase() === (ai.subcategoryName || "").toLowerCase()
                                        ) ||
                                        (list.length
                                            ? new Fuse(list, { keys: ["SubcategoryName"], threshold: 0.3 }).search(ai.subcategoryName)[0]?.item
                                            : null);
                                }
                                inv.SubcategoryID = sub?.SubcategoryID || null;
                                inv.SubcategoryName = sub?.SubcategoryName || ai.subcategoryName || "";
                            } else {
                                inv.CategoryID = inv.CategoryID || null;
                                inv.CategoryName = inv.CategoryName || "";
                                inv.SubcategoryID = inv.SubcategoryID || null;
                                inv.SubcategoryName = inv.SubcategoryName || "";
                            }

                            const key = primaryKey(inv);
                            let apiid = keyToApiRef.current.get(key);
                            if (!apiid) {
                                apiid = uuidv4();
                                keyToApiRef.current.set(key, apiid);
                            }
                            inv.apiid = apiid;

                            const invNo = String(inv.InvoiceNumber || "").trim();
                            if (invNo) {
                                const byVendorKey = makeExpenseKey(inv.VendorName || "", invNo);
                                const byLooseKey = makeExpenseKey("", invNo);
                                const exist =
                                    expenseIndexRef.current.get(byVendorKey) ||
                                    expenseIndexRef.current.get(byLooseKey) ||
                                    null;
                                if (exist) {
                                    inv.__existingExpense = true;
                                    inv.__existingExpenseId = getExpenseId(exist);
                                    inv.__existingExpenseRaw = exist;
                                }
                            }

                            const pageInfo = { url: localURL, pageNum: page?.PageNum || 1, blob: file };
                            setRows((prev) => {
                                const i = prev.findIndex((r) => r.apiid === apiid);
                                if (i >= 0) {
                                    const merged = mergeRows(prev[i], inv, pageInfo);
                                    const clone = prev.slice();
                                    clone[i] = merged;
                                    return clone;
                                } else {
                                    const first = {
                                        ...inv,
                                        imageURL: localURL,
                                        pageImages: [{ url: localURL, pageNum: page?.PageNum || 1, blob: file }],
                                        Items: normalizeItems(inv.Items),
                                        PageNum: page?.PageNum || 1,
                                        pdfURL: null,
                                    };
                                    if (!first.InvoiceTotal && first.Items?.length) {
                                        first.InvoiceTotal = sumItems(first.Items);
                                    }
                                    return [...prev, first];
                                }
                            });
                        })
                    );
                } catch (e) {
                    console.error(`OCR failed for ${file.name}:`, e?.message || e);
                } finally {
                    processCountRef.current += 1;
                    if (mountedRef.current) setProgress(processCountRef.current);
                }
            });

        await Promise.all(tasks.map((t) => doOne(t)));

        setRows((prev) =>
            prev.map((r) => {
                if (safeNumber(r.InvoiceTotal) === 0 && Array.isArray(r.Items) && r.Items.length) {
                    return { ...r, InvoiceTotal: sumItems(r.Items) };
                }
                return r;
            })
        );

        if (mountedRef.current) setLoading(false);
    };

    /* Convert row to expense payload - FIXED: Removed apiid */
    const toExpenses = (r) => {
        const chosenURL = r.pdfURL || r.imageURL || "";
        const imageName = chosenURL ? chosenURL.split("/").pop() : "";
        return {
            // ❌ REMOVED apiid - let database generate it
            ProcessID: "",
            ExpenseDate: r.InvoiceDate || null,
            Amount: safeNumber(r.InvoiceTotal),
            ExpenseCategoryID: r.CategoryID ? String(r.CategoryID) : null,
            ExpenseSubCategoryID: r.SubcategoryID ? String(r.SubcategoryID) : null,
            Description: `Invoice ${r.InvoiceNumber || ""} - ${r.VendorName || ""}`.trim(),
            isActive: true,
            CreatedOn: new Date().toISOString(),
            CreatedBy: "FIG-Agent",
            SchoolDistrictId: null,
            SchoolId: r.SchoolId ? String(r.SchoolId) : null,
            ExpenseCategory: r.CategoryName || "",
            ExpenseSubcategory: r.SubcategoryName || "",
            DistrictName: null,
            SiteName: r.SchoolName || "",
            VendorID: r.VendorID ? String(r.VendorID) : null,
            VendorName: r.VendorName || "",
            catFlag: 1,
            isInvoice: true,
            InvoiceNumber: r.InvoiceNumber || "",
            PONumber: null,
            POAmount: null,
            POType: null,
            InvoiceStatus: null,
            imageURL: chosenURL,
            imageName,
            isCategorized: null,
            isScanned: true,
            ReimbursementTrackingId: null,
            ReimbursmentTrackingProgram: null,
            isPaid: null,
            PaymentDate: null,
            PaymentType: null,
            PaymentInformation: null,
            isVariance: null,
            kpiid: null,
            kpi_categoryname: null,
        };
    };

    /* Export functions */
    const exportHeader = async (r) => {
        const hdr = {
            apiid: r.apiid,
            InvoiceNumber: r.InvoiceNumber,
            VendorName: r.VendorName,
            VendorID: r.VendorID,
            CustomerName: r.CustomerName,
            InvoiceDate: r.InvoiceDate,
            InvoiceTotal: safeNumber(r.InvoiceTotal),
            CategoryID: r.CategoryID || null,
            CategoryName: r.CategoryName || "",
            SubcategoryID: r.SubcategoryID || null,
            SubcategoryName: r.SubcategoryName || "",
            SchoolId: r.SchoolId || null,
            SchoolName: r.SchoolName || "",
            CreatedOn: new Date().toISOString(),
            PageNum: Array.isArray(r.pageImages) ? r.pageImages.length : r.PageNum || 1,
            imageURL: r.pdfURL || r.imageURL || "",
        };
        await axios.post(API_HDR, hdr);
    };

    const exportItems = async (r) => {
        if (!Array.isArray(r.Items) || !r.Items.length) return;

        for (const rawItem of r.Items) {
            const it = {
                apiid: uuidv4(),
                InvoiceApiId: r.apiid,
                InvoiceNumber: r.InvoiceNumber,
                Description: asString(rawItem.Description),
                Quantity: asNumber(rawItem.Quantity),
                Unit: asString(rawItem.Unit, 50),
                UnitPrice: asNumber(rawItem.UnitPrice),
                ContractPrice: asNumber(rawItem.ContractPrice),
                ProductCode: asString(rawItem.ProductCode, 100),
                ItemDate: asDateTime(rawItem.Date),
                Tax: asString(rawItem.Tax, 50),
                Amount: asNumber(rawItem.Amount),
                CreatedOn: new Date().toISOString(),
            };
            await axios.post(API_ITEMS, it);
            await sleep(50);
        }
    };

    const exportExpense = async (r) => {
        const payload = toExpenses(r);
        if (r.__existingExpense && r.__existingExpenseId) {
            try {
                await axios.put(`${API_EXPENSES}/${r.__existingExpenseId}`, payload);
                return;
            } catch (e) {
                console.warn(`PUT update failed for ${r.InvoiceNumber}, falling back to POST:`, e?.message || e);
            }
        }
        await axios.post(API_EXPENSES, payload);
    };

    /* ===============================================
       IMPROVED EXPORT ALL - HANDLES FAILURES GRACEFULLY
       =============================================== */
    const exportAll = async () => {
        if (!rows.length) return;
        setExporting(true);
        setExportProgress(0);
        setExportMessage("Preparing invoices for export...");

        const list = (skipExistingOnExport ? rows.filter((r) => !r.__existingExpense) : rows).slice();
        const successes = []; // Track successfully exported invoice IDs
        const failures = []; // Track failures with details
        let done = 0;

        // Build PDFs
        const pdfLimiter = pLimit(4);
        await Promise.all(
            list.map((r) =>
                pdfLimiter(async () => {
                    try {
                        if (!r.pdfURL && Array.isArray(r.pageImages) && r.pageImages.length > 0) {
                            const combined = await buildAndUploadInvoicePdf(r);
                            if (combined) {
                                r.pdfURL = combined;
                                setRows((prev) => prev.map((row) => (row.apiid === r.apiid ? { ...row, pdfURL: combined } : row)));
                            }
                        }
                    } catch (e) {
                        console.error("PDF build error:", r.InvoiceNumber || r.apiid, e);
                    }
                })
            )
        );

        setExportMessage("Uploading invoices to database...");

        // Retry with exponential backoff
        const retryWithBackoff = async (fn, maxRetries = 5, invoiceNum = "") => {
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    return await fn();
                } catch (error) {
                    const isDeadlock =
                        error?.response?.status === 409 ||
                        error?.response?.status === 423 ||
                        error?.response?.data?.error?.toLowerCase?.()?.includes('deadlock') ||
                        error?.message?.toLowerCase()?.includes('deadlock');

                    if (isDeadlock && attempt < maxRetries - 1) {
                        const delay = Math.min(2000 * Math.pow(2, attempt), 10000);
                        console.warn(`Retrying ${invoiceNum} after ${delay}ms...`);
                        await sleep(delay);
                    } else {
                        throw error;
                    }
                }
            }
        };

        // Sequential export with failure tracking
        for (const r of list) {
            if (exportedRef.current.has(r.apiid)) {
                successes.push(r.apiid);
                done++;
                if (mountedRef.current) setExportProgress(done);
                continue;
            }

            try {
                console.log(`Exporting invoice ${done + 1}/${list.length}: ${r.InvoiceNumber || r.apiid}`);

                await retryWithBackoff(
                    () => exportHeader(r),
                    5,
                    r.InvoiceNumber || r.apiid
                );

                await sleep(100);

                await retryWithBackoff(
                    () => exportItems(r),
                    5,
                    r.InvoiceNumber || r.apiid
                );

                await sleep(100);

                await retryWithBackoff(
                    () => exportExpense(r),
                    5,
                    r.InvoiceNumber || r.apiid
                );

                exportedRef.current.add(r.apiid);
                successes.push(r.apiid);
                console.log(`✅ Successfully exported: ${r.InvoiceNumber || r.apiid}`);

                await sleep(300);

            } catch (e) {
                console.error(`❌ Failed to export: ${r.InvoiceNumber || r.apiid}`, e?.response?.data || e?.message || e);

                // Capture detailed failure information
                failures.push({
                    apiid: r.apiid,
                    invoiceNumber: r.InvoiceNumber || 'N/A',
                    vendorName: r.VendorName || 'Unknown',
                    error: e?.response?.data?.error || e?.message || 'Unknown error'
                });
            } finally {
                revokeObjectURLsForRow(r);
                done++;
                if (mountedRef.current) setExportProgress(done);
            }
        }

        setExporting(false);

        // Build comprehensive result message
        const successCount = successes.length;
        const failCount = failures.length;

        if (failCount > 0 && successCount > 0) {
            // Partial success - show both successes and failures
            const failureDetails = failures.map(f =>
                `• ${f.invoiceNumber} (${f.vendorName}): ${f.error}`
            ).join('\n');

            setError(
                `⚠️ Partial Export Completed\n\n` +
                `✅ Successfully exported: ${successCount} invoice(s)\n` +
                `❌ Failed to export: ${failCount} invoice(s)\n\n` +
                `Failed invoices:\n${failureDetails}\n\n` +
                `The failed invoices remain in the grid for you to review and retry.`
            );

            // Remove ONLY successfully exported rows from the grid
            setRows((prev) => prev.filter((r) => !successes.includes(r.apiid)));

            // Only clear files/pages if ALL were processed
            if (successes.length === list.length) {
                setFiles([]);
                setPdfPages([]);
            }

            setExportMessage(`⚠️ Partial: ${successCount} exported, ${failCount} failed`);
            setTimeout(() => setExportMessage(""), 5000);

        } else if (failCount > 0) {
            // All failed - keep everything in grid
            const failureDetails = failures.map(f =>
                `• ${f.invoiceNumber} (${f.vendorName}): ${f.error}`
            ).join('\n');

            setError(
                `❌ Export Failed\n\n` +
                `All ${failCount} invoice(s) failed to export:\n\n${failureDetails}\n\n` +
                `Please review the errors and try again.`
            );
            setExportMessage("");

        } else {
            // All succeeded - clear everything
            setRows([]);
            setFiles([]);
            setPdfPages([]);
            exportedRef.current.clear();
            keyToApiRef.current.clear();

            setExportMessage(`✅ Success! All ${successCount} invoices exported.`);
            setTimeout(() => setExportMessage(""), 3000);
        }
    };

    /* Row editing */
    const processRowUpdate = (next, prev) => {
        const r = { ...next };

        if (next.VendorName !== prev.VendorName) {
            const v =
                vendors.find((x) => x.VendorName === next.VendorName) ||
                vendors.find((x) => (x.VendorName || "").toLowerCase() === String(next.VendorName || "").toLowerCase());
            r.VendorID = v?.VendorID || null;
        }

        if (next.SchoolName !== prev.SchoolName) {
            const s = schools.find((x) => x.SchoolName === next.SchoolName);
            r.SchoolId = s?.SchoolId || null;
            r.SchoolName = s?.SchoolName || next.SchoolName || "";
        }

        if (next.CategoryName !== prev.CategoryName) {
            const c =
                categories.find((x) => x.CategoryName === next.CategoryName) ||
                categories.find((x) => (x.CategoryName || "").toLowerCase() === String(next.CategoryName || "").toLowerCase());
            r.CategoryID = c?.CategoryID || null;

            const list = r.CategoryID ? subsByCat.get(String(r.CategoryID)) || [] : [];
            const stillValid = list.find((su) => su.SubcategoryName === r.SubcategoryName);
            if (!stillValid) {
                r.SubcategoryID = null;
                r.SubcategoryName = "";
            }
        }

        if (next.SubcategoryName !== prev.SubcategoryName || next.CategoryID !== prev.CategoryID) {
            const list = r.CategoryID ? subsByCat.get(String(r.CategoryID)) || [] : [];
            const sub = list.find((su) => su.SubcategoryName === next.SubcategoryName);
            r.SubcategoryID = sub?.SubcategoryID || null;
            r.SubcategoryName = sub?.SubcategoryName || next.SubcategoryName || "";
        }

        r.InvoiceTotal = safeNumber(r.InvoiceTotal);

        if (next.InvoiceNumber !== prev.InvoiceNumber || next.VendorName !== prev.VendorName) {
            const invNo = String(r.InvoiceNumber || "").trim();
            if (invNo) {
                const byVendorKey = makeExpenseKey(r.VendorName || "", invNo);
                const byLooseKey = makeExpenseKey("", invNo);
                const exist =
                    expenseIndexRef.current.get(byVendorKey) ||
                    expenseIndexRef.current.get(byLooseKey) ||
                    null;
                r.__existingExpense = !!exist;
                r.__existingExpenseId = exist ? getExpenseId(exist) : null;
                r.__existingExpenseRaw = exist || null;
            } else {
                r.__existingExpense = false;
                r.__existingExpenseId = null;
                r.__existingExpenseRaw = null;
            }
        }

        return r;
    };

    const onRowUpdateCommit = (updatedRow) => {
        setRows((prev) => prev.map((x) => (x.apiid === updatedRow.apiid ? updatedRow : x)));
        return updatedRow;
    };

    /* Review modal */
    const openReview = (row) => {
        setZoom(1);
        setReviewIndex(0);
        const snapshot = JSON.parse(JSON.stringify(row || {}));
        setReviewData(snapshot);
        setReviewErrors({});
        setReviewOpen(true);
    };

    const closeReview = () => {
        setReviewOpen(false);
        setReviewData(null);
        setReviewErrors({});
        setReviewIndex(0);
    };

    const validateReview = (draft) => {
        const errs = {};
        if (draft.InvoiceDate) {
            const validPattern = /^\d{4}-\d{2}-\d{2}$/;
            if (!validPattern.test(String(draft.InvoiceDate))) {
                errs.InvoiceDate = "Use YYYY-MM-DD";
            }
        }
        if (draft.InvoiceTotal !== undefined && draft.InvoiceTotal !== null) {
            const num = Number(draft.InvoiceTotal);
            if (!Number.isFinite(num)) errs.InvoiceTotal = "Enter a valid amount";
            if (num < 0) errs.InvoiceTotal = "Amount cannot be negative";
        }
        if (draft.SubcategoryName && draft.CategoryID) {
            const list = subsByCat.get(String(draft.CategoryID)) || [];
            const ok = list.some((su) => su.SubcategoryName.toLowerCase() === String(draft.SubcategoryName).toLowerCase());
            if (!ok) errs.SubcategoryName = "Not in selected Category";
        }
        return errs;
    };

    const applyReviewField = (field, value) => {
        setReviewData((prev) => {
            const next = { ...prev, [field]: value };
            const synced = processRowUpdate(next, prev);
            const errs = validateReview(synced);
            setReviewErrors(errs);
            return synced;
        });
    };

    const saveReview = () => {
        if (!reviewData) return;
        const original = rows.find((r) => r.apiid === reviewData.apiid) || reviewData;
        const synced = processRowUpdate(reviewData, original);
        const errs = validateReview(synced);
        setReviewErrors(errs);
        if (Object.keys(errs).length) return;
        onRowUpdateCommit(synced);
        closeReview();
    };

    /* Gallery */
    const openGallery = (row, startIndex = 0) => {
        setGalleryRow(row);
        setGalleryIndex(startIndex);
        setGalleryOpen(true);
    };
    const closeGallery = () => {
        setGalleryOpen(false);
        setGalleryRow(null);
        setGalleryIndex(0);
    };
    const nextPage = () => {
        if (!galleryRow?.pageImages?.length) return;
        setGalleryIndex((i) => (i + 1) % galleryRow.pageImages.length);
    };
    const prevPage = () => {
        if (!galleryRow?.pageImages?.length) return;
        setGalleryIndex((i) => (i - 1 + galleryRow.pageImages.length) % galleryRow.pageImages.length);
    };

    /* Counts */
    const counts = useMemo(() => {
        let updates = 0;
        for (const r of rows) if (r.__existingExpense) updates++;
        return { updates, fresh: rows.length - updates, total: rows.length };
    }, [rows]);

    /* Columns */
    const columns = [
        {
            field: "actions",
            headerName: "Review",
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (p) => (
                <Button
                    variant="contained"
                    sx={reviewGlowSx}
                    startIcon={<EditOutlinedIcon />}
                    onClick={(e) => {
                        e.stopPropagation();
                        openReview(p.row);
                    }}
                    aria-label="Review and edit invoice"
                >
                    Review
                </Button>
            ),
        },
        {
            field: "__status",
            headerName: "Status",
            width: 170,
            sortable: false,
            filterable: true,
            renderCell: (p) => (
                <Chip
                    label={p.row.__existingExpense ? "Updating Existing" : "New"}
                    color={p.row.__existingExpense ? "warning" : "primary"}
                    size="small"
                    variant={p.row.__existingExpense ? "filled" : "outlined"}
                />
            ),
        },
        {
            field: "imageURL",
            headerName: "Image",
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (p) => {
                const hasPages = Array.isArray(p.row.pageImages) && p.row.pageImages.length > 0;
                const cover = hasPages ? p.row.pageImages[0]?.url : p.row.imageURL || "";
                return (
                    <Thumb
                        src={cover || ""}
                        alt="invoice"
                        onClick={() => hasPages && openGallery(p.row, 0)}
                        style={{ opacity: cover ? 1 : 0.25 }}
                        title={hasPages ? `View ${p.row.pageImages.length} page(s)` : "No image"}
                    />
                );
            },
        },
        {
            field: "pageCount",
            headerName: "Pages",
            width: 90,
            valueGetter: (p) => (Array.isArray(p.row.pageImages) ? p.row.pageImages.length : p.row.PageNum || 1),
        },
        { field: "apiid", headerName: "API ID", width: 220, editable: true },
        {
            field: "VendorName",
            headerName: "Vendor",
            width: 220,
            editable: true,
            type: "singleSelect",
            valueOptions: vendors.map((v) => v.VendorName),
        },
        { field: "VendorID", headerName: "Vendor ID", width: 140, editable: true },
        { field: "CustomerName", headerName: "Ship To", width: 260, editable: true },
        { field: "InvoiceNumber", headerName: "Invoice #", width: 160, editable: true },
        { field: "InvoiceDate", headerName: "Invoice Date", width: 150, editable: true },
        {
            field: "InvoiceTotal",
            headerName: "Total",
            width: 130,
            editable: true,
            valueFormatter: (p) =>
                p.value !== undefined && p.value !== null
                    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(p.value)
                    : "",
        },
        {
            field: "CategoryName",
            headerName: "Category",
            width: 200,
            editable: true,
            type: "singleSelect",
            valueOptions: categories.map((c) => c.CategoryName),
        },
        { field: "CategoryID", headerName: "Category ID", width: 130, editable: true },
        {
            field: "SubcategoryName",
            headerName: "Subcategory",
            width: 220,
            editable: true,
            type: "singleSelect",
            valueOptions: (params) => {
                const catId = params.row.CategoryID;
                const list = catId ? subsByCat.get(String(catId)) || [] : [];
                return list.map((s) => s.SubcategoryName);
            },
        },
        { field: "SubcategoryID", headerName: "Subcat ID", width: 130, editable: true },
        {
            field: "SchoolName",
            headerName: "Site",
            width: 240,
            editable: true,
            type: "singleSelect",
            valueOptions: schools.map((s) => s.SchoolName),
        },
        { field: "SchoolId", headerName: "Site ID", width: 140, editable: true },
        { field: "__existingExpenseId", headerName: "Expense ID", width: 180, editable: false, hide: true },
    ];

    /* Render */
    return (
        <Box
            sx={{
                m: 2,
                p: 2,
                bgcolor: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderRadius: 2,
                boxShadow: theme.shadows[3],
                minHeight: "100vh",
            }}
        >
            <Header
                title="Invoice Bulk Review & Processing"
                subtitle="Upload, review, and export invoices with AI-powered categorization"
            />

            {/* User Instructions */}
            <Box
                sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 3,
                    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
                    color: "#fff",
                    boxShadow: `0 0 15px 4px ${alpha(theme.palette.secondary.main, 0.3)}`,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    📋 How to Use This Page
                </Typography>
                <Typography variant="body2" component="div" sx={{ lineHeight: 1.8 }}>
                    <strong>Step 1:</strong> Upload invoice files (PDF or images) using drag & drop or the file browser<br />
                    <strong>Step 2:</strong> Click "Process Invoices" to extract data using AI-powered OCR<br />
                    <strong>Step 3:</strong> Review each invoice using the "Review" button - verify vendor, site, amounts, and categories<br />
                    <strong>Step 4:</strong> Edit any fields directly in the grid or in the review dialog<br />
                    <strong>Step 5:</strong> Click "Export to Database" to save all invoices<br /><br />
                    <strong>💡 Tips:</strong><br />
                    • Multi-page PDFs are automatically split for better OCR accuracy<br />
                    • AI automatically matches vendors, sites, and suggests categories<br />
                    • Use "Skip existing" to only export new invoices<br />
                    • The system detects and updates existing invoices automatically<br />
                    • <strong>If an invoice fails to export, the batch continues and failed invoices remain in the grid for retry</strong>
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {exportMessage && (
                <Alert severity={exportMessage.includes('⚠️') ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setExportMessage("")}>
                    {exportMessage}
                </Alert>
            )}

            <DropZone
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Upload area"
            >
                📁 Drag & drop invoices here, or click to browse
            </DropZone>

            <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFiles}
                    style={{ display: "none" }}
                />
                <Button variant="contained" onClick={() => fileInputRef.current?.click()}>
                    Choose Files
                </Button>
                <Button variant="contained" color="primary" onClick={handleProcess} disabled={loading}>
                    {loading ? "Processing…" : "Process Invoices"}
                </Button>

                {rows.length > 0 && (
                    <>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={exportAll}
                            disabled={exporting || (skipExistingOnExport && rows.every(r => r.__existingExpense))}
                        >
                            {exporting ? `Exporting… ${exportProgress}/${skipExistingOnExport ? rows.filter(r => !r.__existingExpense).length : rows.length}` : "Export to Database"}
                        </Button>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={skipExistingOnExport}
                                    onChange={(e) => setSkipExistingOnExport(e.target.checked)}
                                />
                            }
                            label="Skip existing invoices"
                        />
                    </>
                )}
            </Box>

            {!!rows.length && (
                <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip label={`Total: ${counts.total}`} />
                    <Chip color="primary" variant="outlined" label={`New: ${counts.fresh}`} />
                    <Chip color="warning" label={`Updates: ${counts.updates}`} />
                    {!expenseIndexLoaded && (
                        <Chip color="error" label="⚠️ Unable to detect existing invoices" size="small" />
                    )}
                </Box>
            )}

            <Box display="flex" gap={1} mb={2} sx={{ overflowX: "auto" }}>
                {files.map((f, i) => <Thumb key={`f-${i}`} src={URL.createObjectURL(f)} alt={`upload ${i}`} />)}
                {pdfPages.map((p, i) => <Thumb key={`p-${i}`} src={URL.createObjectURL(p.file)} alt={`pdf page ${i}`} />)}
            </Box>

            {/* Processing modal */}
            <Modal open={loading} hideBackdrop>
                <Box
                    sx={{
                        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                        bgcolor: theme.palette.background.paper, p: 3, borderRadius: 2, textAlign: "center",
                        minWidth: 300,
                    }}
                >
                    <CircularProgress size={48} sx={{ color: theme.palette.secondary.main }} />
                    <Typography mt={2} variant="h6">
                        Processing Invoice {progress} of {files.length + pdfPages.length}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: .75 }}>
                        Extracting data with AI-powered OCR
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: .5, opacity: .75 }}>
                        Matching vendors, sites, and categories
                    </Typography>
                </Box>
            </Modal>

            {/* Exporting modal */}
            <Modal open={exporting} hideBackdrop>
                <Box
                    sx={{
                        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                        bgcolor: theme.palette.background.paper, p: 3, borderRadius: 2, textAlign: "center",
                        minWidth: 300,
                    }}
                >
                    <CircularProgress size={48} />
                    <Typography mt={2} variant="h6">
                        {exportMessage}
                    </Typography>
                    <Typography variant="body2" mt={1}>
                        {exportProgress} of {skipExistingOnExport ? rows.filter(r => !r.__existingExpense).length : rows.length} invoices
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: .75 }}>
                        Processing continues even if individual invoices fail
                    </Typography>
                </Box>
            </Modal>

            {/* GRID */}
            <Box mt={4}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(r) => r.apiid}
                    autoHeight
                    pageSize={25}
                    rowsPerPageOptions={[25, 50, 100]}
                    components={{ Toolbar: GridToolbar }}
                    disableSelectionOnClick
                    experimentalFeatures={{ newEditingApi: true }}
                    processRowUpdate={(n, o) => {
                        const upd = processRowUpdate(n, o);
                        onRowUpdateCommit(upd);
                        return upd;
                    }}
                    onRowDoubleClick={(params) => openReview(params.row)}
                    onProcessRowUpdateError={(e) => console.error(e)}
                    getRowClassName={(params) => (params.row.__existingExpense ? "row-updating" : "")}
                    sx={{
                        "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: theme.palette.background.paper },
                        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: theme.palette.action.hover },
                        "& .row-updating": {
                            background: `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.18)} 0%, transparent 75%)`,
                        },
                    }}
                />
            </Box>

            {/* Gallery Modal */}
            <Modal open={galleryOpen} onClose={closeGallery}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        bgcolor: theme.palette.background.paper,
                        p: 2,
                        borderRadius: 2,
                        maxWidth: "92vw",
                        maxHeight: "92vh",
                    }}
                >
                    {galleryRow?.pageImages?.length ? (
                        <Box sx={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle2">
                                    {galleryRow.InvoiceNumber || "Invoice"} — page {galleryIndex + 1} of {galleryRow.pageImages.length}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button onClick={prevPage} size="small">◀ Prev</Button>
                                    <Button onClick={nextPage} size="small">Next ▶</Button>
                                    <IconButton aria-label="Close" onClick={closeGallery}>
                                        <CloseIcon />
                                    </IconButton>
                                </Stack>
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    overflow: "auto",
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: theme.palette.background.paper,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <img
                                    src={galleryRow.pageImages[galleryIndex].url}
                                    alt="Invoice page"
                                    style={{ maxWidth: "88vw", maxHeight: "76vh", display: "block" }}
                                />
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                {galleryRow.pageImages.map((p, i) => (
                                    <img
                                        key={p.url}
                                        src={p.url}
                                        alt={`thumb ${i + 1}`}
                                        onClick={() => setGalleryIndex(i)}
                                        style={{
                                            width: 64,
                                            height: 64,
                                            objectFit: "cover",
                                            borderRadius: 6,
                                            border: i === galleryIndex ? "2px solid #00E5FF" : `1px solid ${theme.palette.divider}`,
                                            cursor: "pointer",
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Typography>No images.</Typography>
                    )}
                </Box>
            </Modal>

            {/* Review Dialog */}
            <Dialog
                open={reviewOpen}
                onClose={closeReview}
                fullWidth
                maxWidth="xl"
                aria-labelledby="review-edit-dialog-title"
            >
                <DialogTitle id="review-edit-dialog-title" sx={{ pr: 6 }}>
                    Review & Edit Invoice
                    <IconButton
                        aria-label="Close"
                        onClick={closeReview}
                        sx={{ position: "absolute", right: 12, top: 10 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
                            gap: 0,
                            minHeight: { md: "70vh" },
                        }}
                    >
                        {/* LEFT: Image pane */}
                        <Box
                            sx={{
                                p: 2,
                                borderRight: { md: `1px solid ${theme.palette.divider}` },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle2">
                                    {reviewData?.pageImages?.length
                                        ? `Page ${reviewIndex + 1}/${reviewData.pageImages.length}`
                                        : reviewData?.imageURL ? "Invoice image" : "No image"}
                                </Typography>
                                {!!(reviewData?.pageImages?.length || reviewData?.imageURL) && (
                                    <Tooltip title="Open in new tab">
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={reviewData?.pageImages?.length ? reviewData.pageImages[reviewIndex]?.url : reviewData?.imageURL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {reviewData?.pageImages?.length > 1 && (
                                <Stack direction="row" spacing={1} sx={{ width: "100%", justifyContent: "flex-end" }}>
                                    <Button size="small" onClick={() => setReviewIndex((i) => Math.max(0, i - 1))} disabled={reviewIndex === 0}>
                                        ◀ Prev
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setReviewIndex((i) => Math.min(reviewData.pageImages.length - 1, i + 1))}
                                        disabled={reviewIndex >= reviewData.pageImages.length - 1}
                                    >
                                        Next ▶
                                    </Button>
                                </Stack>
                            )}

                            <Box
                                sx={{
                                    width: "100%",
                                    overflow: "auto",
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: theme.palette.background.paper,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {reviewData?.pageImages?.length ? (
                                    <img
                                        src={reviewData.pageImages[reviewIndex]?.url}
                                        alt="Invoice"
                                        style={{
                                            transform: `scale(${zoom})`,
                                            transformOrigin: "top left",
                                            maxWidth: "100%",
                                            height: "auto",
                                            display: "block",
                                        }}
                                    />
                                ) : reviewData?.imageURL ? (
                                    <img
                                        src={reviewData.imageURL}
                                        alt="Invoice"
                                        style={{
                                            transform: `scale(${zoom})`,
                                            transformOrigin: "top left",
                                            maxWidth: "100%",
                                            height: "auto",
                                            display: "block",
                                        }}
                                    />
                                ) : (
                                    <Typography sx={{ p: 2 }}>No image available</Typography>
                                )}
                            </Box>

                            <Box sx={{ width: "100%", px: 1 }}>
                                <Typography variant="caption">Zoom</Typography>
                                <Slider
                                    min={0.5}
                                    max={2.5}
                                    step={0.1}
                                    value={zoom}
                                    onChange={(_, v) => setZoom(Number(v))}
                                    aria-label="Zoom"
                                />
                            </Box>
                        </Box>

                        {/* RIGHT: Form pane */}
                        <Box sx={{ p: 2, overflowY: "auto" }}>
                            {reviewData ? (
                                <Stack spacing={2}>
                                    {reviewData.__existingExpense && (
                                        <Alert severity="warning">
                                            This invoice already exists and will be <b>updated</b> when exported
                                        </Alert>
                                    )}

                                    <TextField
                                        label="API ID"
                                        value={reviewData.apiid || ""}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                        size="small"
                                    />

                                    <Autocomplete
                                        freeSolo
                                        options={vendors.map((v) => v.VendorName)}
                                        value={reviewData.VendorName || ""}
                                        onChange={(_, val) => applyReviewField("VendorName", val || "")}
                                        onInputChange={(_, val) => applyReviewField("VendorName", val || "")}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Vendor" fullWidth size="small" />
                                        )}
                                    />
                                    <TextField
                                        label="Vendor ID"
                                        value={reviewData.VendorID || ""}
                                        InputProps={{ readOnly: true }}
                                        helperText={reviewData.VendorName && !reviewData.VendorID ? "⚠️ Vendor not matched" : " "}
                                        fullWidth
                                        size="small"
                                    />

                                    <Autocomplete
                                        freeSolo
                                        options={schools.map((s) => s.SchoolName)}
                                        value={reviewData.SchoolName || ""}
                                        onChange={(_, val) => applyReviewField("SchoolName", val || "")}
                                        onInputChange={(_, val) => applyReviewField("SchoolName", val || "")}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Site" fullWidth size="small" />
                                        )}
                                    />
                                    <TextField
                                        label="Site ID"
                                        value={reviewData.SchoolId || ""}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                        size="small"
                                    />

                                    <Autocomplete
                                        freeSolo
                                        options={categories.map((c) => c.CategoryName)}
                                        value={reviewData.CategoryName || ""}
                                        onChange={(_, val) => applyReviewField("CategoryName", val || "")}
                                        onInputChange={(_, val) => applyReviewField("CategoryName", val || "")}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Category" fullWidth size="small" />
                                        )}
                                    />
                                    <TextField
                                        label="Category ID"
                                        value={reviewData.CategoryID || ""}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                        size="small"
                                    />

                                    <Autocomplete
                                        key={String(reviewData.CategoryID || "none")}
                                        freeSolo
                                        options={(reviewData.CategoryID ? (subsByCat.get(String(reviewData.CategoryID)) || []) : [])
                                            .map((su) => su.SubcategoryName)}
                                        value={reviewData.SubcategoryName || ""}
                                        onChange={(_, val) => applyReviewField("SubcategoryName", val || "")}
                                        onInputChange={(_, val) => applyReviewField("SubcategoryName", val || "")}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Subcategory"
                                                fullWidth
                                                size="small"
                                                error={Boolean(reviewErrors.SubcategoryName)}
                                                helperText={reviewErrors.SubcategoryName || " "}
                                            />
                                        )}
                                        disabled={!reviewData.CategoryID}
                                    />
                                    <TextField
                                        label="Subcategory ID"
                                        value={reviewData.SubcategoryID || ""}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                        size="small"
                                    />

                                    <TextField
                                        label="Invoice Number"
                                        value={reviewData.InvoiceNumber || ""}
                                        onChange={(e) => applyReviewField("InvoiceNumber", e.target.value)}
                                        fullWidth
                                        size="small"
                                    />

                                    <TextField
                                        label="Invoice Date"
                                        type="date"
                                        value={reviewData.InvoiceDate || ""}
                                        onChange={(e) => applyReviewField("InvoiceDate", e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        error={Boolean(reviewErrors.InvoiceDate)}
                                        helperText={reviewErrors.InvoiceDate || " "}
                                    />

                                    <TextField
                                        label="Invoice Total"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        value={reviewData.InvoiceTotal ?? ""}
                                        onChange={(e) => applyReviewField("InvoiceTotal", e.target.value)}
                                        fullWidth
                                        size="small"
                                        error={Boolean(reviewErrors.InvoiceTotal)}
                                        helperText={reviewErrors.InvoiceTotal || " "}
                                    />

                                    <TextField
                                        label="Ship To"
                                        value={reviewData.CustomerName || ""}
                                        onChange={(e) => applyReviewField("CustomerName", e.target.value)}
                                        fullWidth
                                        size="small"
                                    />

                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            label="Pages"
                                            value={Array.isArray(reviewData.pageImages) ? reviewData.pageImages.length : reviewData.PageNum || 1}
                                            InputProps={{ readOnly: true }}
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />
                                        <TextField
                                            label="Image URL"
                                            value={reviewData.imageURL || ""}
                                            InputProps={{ readOnly: true }}
                                            size="small"
                                            sx={{ flex: 3 }}
                                        />
                                    </Stack>
                                </Stack>
                            ) : (
                                <Typography sx={{ p: 2 }}>No data</Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeReview}>Cancel</Button>
                    <Button
                        onClick={saveReview}
                        variant="contained"
                        disabled={Boolean(Object.keys(reviewErrors).length)}
                    >
                        💾 Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}